import React, {Component} from 'react'
import propTypes from 'prop-types'
import {isNil} from 'lodash'
import DayPicker from "react-day-picker"
import "react-day-picker/lib/style.css"
import Button from 'components/button'
import './calendarDatePicker.scss'
import {connect} from 'react-redux'
import LocaleUtils from 'react-day-picker/moment'
import ReactDOM from 'react-dom'
import {getRegionalLocale} from 'selectors/settings'

const focusCalendar = (instance) => {
  if (instance) {
    const element = ReactDOM.findDOMNode(instance)

    if (element) {
      let dayElement = element.querySelector(".DayPicker-Day--selected")
      if (dayElement === null) dayElement = element.querySelector(".DayPicker-Day--today")

      if (dayElement) {
        dayElement.focus()
      } else {
        element.focus()
      }
    }
  }
}

const currentYear = new Date().getFullYear()
const fromMonth = new Date(currentYear - 100, 0)
const toMonth = new Date(currentYear + 100, 11)

const YearMonthForm = ({date, localeUtils, onChange}) => {
  const months = localeUtils.getMonths()

  const years = []
  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i)
  }

  const handleChange = (e) => {
    const {year, month} = e.target.form
    onChange(new Date(year.value, month.value))
  }

  const formSubmit = (e) => {
    e.preventDefault()
    return false
  }

  return (
      <form className="DayPicker-Caption" onSubmit={formSubmit}>
        <select name="month" onChange={handleChange} value={date.getMonth()}>
          {months.map((month, i) => (
              <option key={month} value={i}>
                {month}
              </option>
          ))}
        </select>
        <select name="year" onChange={handleChange} value={date.getFullYear()}>
          {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
          ))}
        </select>
      </form>
  )
}

YearMonthForm.propTypes = {
  date: propTypes.object,
  localeUtils: propTypes.object,
  onChange: propTypes.func,
}

const updateStateFromProps = (newProps) => {
  const {date} = newProps
  return {
    month: date,
  }
}

class CalendarDatePicker extends Component {
  constructor(props) {
    super(props)
    this.state = updateStateFromProps(props)
  }

  componentDidMount = () => {
    const {autoFocus} = this.props

    if (autoFocus) focusCalendar(this)
  }

  onDayClick = (newDate) => {
    this.props.onUpdate(newDate)
  }

  goToToday = () => {
    const {date, onUpdate, onMonthChange} = this.props
    const now = new Date()
    const isDateChanged = date && now.getMonth() !== date.getMonth() ||
        now.getFullYear() !== date.getFullYear()

    if (!isNil(onMonthChange) && isDateChanged)
      onMonthChange(now)

    onUpdate(now)
  }

  onMonthChange = (newDate) => {
    const {changeOnMonthSelection, onUpdate, onMonthChange} = this.props
    this.setState({month: newDate}, () => {
      if (onMonthChange) onMonthChange(newDate)
      if (changeOnMonthSelection) {
        onUpdate(newDate)
      }
    })
  }

  renderDay = (date) => {
    const {highlightedDates} = this.props

    let marker
    if (!isNil(highlightedDates)) {
      const time = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          0,
          0,
          0,
          0
      ).getTime()
      if (highlightedDates.indexOf(time) >= 0) {
        marker = <div className='calendarDatePicker-marker'>●</div>
      }
    }

    return (
        <div>
          {marker}
          <div>
            {date.getDate()}
          </div>
        </div>
    )
  }

  keyDown = (e) => {
    if (e.keyCode === 27 && this.props.onCancel) this.props.onCancel()
  }

  render() {
    const {date, range, locale, allowNone, onUpdate, showSelectedRange} = this.props

    let selectedDays = null
    if (showSelectedRange) {
      selectedDays = date
      switch (range) {

        case 'month': {
          const after = new Date(date.getFullYear(), date.getMonth(), 1)
          after.setDate(after.getDate() - 1)
          const before = new Date(date.getFullYear(), date.getMonth() + 1, 1)
          selectedDays = [{after, before}]
          break
        }

        case 'week': {
          const currentWeekDay = date.getDay() + 1
          const lessDays = (currentWeekDay === 0) ? 6 : currentWeekDay - 1
          const after = new Date(new Date(date).setDate(date.getDate() - lessDays))
          const before = new Date(new Date(after).setDate(after.getDate() + 8))
          selectedDays = [{after, before}]
          break
        }
      }
    }

    let noneButton
    if (allowNone) {
      noneButton = <Button flat={true}
          label="Clear"
          onClick={() => onUpdate(null)}/>
    }

    return (
        <div
            ref={e => {
              this.container = e
            }}
            tabIndex={1}>
          <DayPicker
              className='calendarDatePicker'
              month={this.state.month}
              fromMonth={fromMonth}
              toMonth={toMonth}
              selectedDays={selectedDays}
              enableOutsideDays
              fixedWeeks={true}
              onDayClick={this.onDayClick}
              onMonthChange={this.onMonthChange}
              renderDay={this.renderDay}
              onKeyDown={this.keyDown}
              locale={locale}
              localeUtils={LocaleUtils}
              firstDayOfWeek={1}
              captionElement={<YearMonthForm onChange={this.onMonthChange}/>}
          />
          <div className="calendarDatePicker-buttons">
            <Button flat={true}
                label="Today"
                onClick={(e) => this.goToToday(e)}
                primary/>
            {noneButton}
          </div>
        </div>
    )
  }
}

CalendarDatePicker.propTypes = {
  date: propTypes.instanceOf(Date),
  range: propTypes.oneOf(['day', 'week', 'month']),
  changeOnMonthSelection: propTypes.bool,
  allowNone: propTypes.bool,
  locale: propTypes.string,
  autoFocus: propTypes.bool,
  highlightedDates: propTypes.arrayOf(propTypes.number),
  showSelectedRange: propTypes.bool,
  onUpdate: propTypes.func.isRequired,
  onCancel: propTypes.func, // optionally implementation from pressing 'ESC' key
  onMonthChange: propTypes.func,
}

CalendarDatePicker.defaultProps = {
  date: new Date(),
  range: 'day',
  showSelectedRange: true,
  changeOnMonthSelection: false,
  allowNone: false,
  autoFocus: false,
}

const mapStateToProps = (state) => {
  return {
    locale: getRegionalLocale(state),
  }
}

const CalendarDatePickerContainer = connect(mapStateToProps)(CalendarDatePicker)
export default CalendarDatePickerContainer

export {focusCalendar}
